import React, { useState, useEffect } from 'react';
import { useProcurement } from '@/context/ProcurementContext';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  User, 
  Phone, 
  MapPin, 
  Clock, 
  CheckCircle, 
  AlertTriangle, 
  Package,
  FileText,
  Bell,
  Timer
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '@/lib/utils';

const ManagerSection = () => {
  const { state, actions } = useProcurement();
  const [showReceivedModal, setShowReceivedModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [expiredNotifications, setExpiredNotifications] = useState<any[]>([]);

  useEffect(() => {
    actions.fetchInvoices();
    actions.fetchHandoverLogs();
    checkExpiredInvoices();
  }, []);

  // Filter invoices that have passed Quality Testing (IQC) and are ready for Manager
  const managerInvoices = state.invoices.filter(invoice => {
    return invoice.qualityTested === true && 
           (invoice.status === 'Fully Accepted' || invoice.status === 'Partly Accepted') &&
           invoice.receivedStatus === 'Received' &&
           invoice.qualityRating >= 3.0 &&
           // Only show invoices that have NOT been completed yet
           invoice.status !== 'Complete';
  });

  // Check for expired invoices (24+ hours old)
  const checkExpiredInvoices = () => {
    const now = Date.now();
    const expired = managerInvoices.filter(invoice => {
      const createdTime = new Date(invoice.createdAt).getTime();
      const hoursElapsed = (now - createdTime) / (1000 * 60 * 60);
      return hoursElapsed >= 24 && invoice.status !== 'Complete';
    });

    setExpiredNotifications(expired);
    
    // Show notification for new expired invoices
    if (expired.length > 0) {
      toast.error(`${expired.length} invoices have expired (24+ hours old)`, {
        duration: 5000,
      });
    }
  };

  // Update expired notifications every minute
  useEffect(() => {
    const interval = setInterval(checkExpiredInvoices, 60000);
    return () => clearInterval(interval);
  }, [managerInvoices]);

  const handleReceivedClick = (invoice: any) => {
    setSelectedInvoice(invoice);
    setShowReceivedModal(true);
  };

  const confirmReceived = async () => {
    if (!selectedInvoice) return;

    try {
      // Update invoice status to Complete
      await actions.updateInvoice(selectedInvoice.id, {
        status: 'Complete',
        receivedAt: new Date().toISOString(),
        receivedBy: state.currentUser?.id || 1,
        receivedByName: state.currentUser?.name || 'Manager',
        updatedAt: new Date().toISOString()
      });

      // Update related POs that share the same PO ID
      const relatedPOs = state.purchaseOrders.filter(po => po.poNumber === selectedInvoice.poNumber);
      for (const po of relatedPOs) {
        await actions.updatePurchaseOrder(po.id, {
          status: 'FULLY_RECEIVED',
          updatedAt: new Date().toISOString()
        });
      }

      toast.success(`Invoice ${selectedInvoice.invoiceNumber} marked as received and completed`);
      setShowReceivedModal(false);
      setSelectedInvoice(null);
      
      // Refresh data
      actions.fetchInvoices();
      actions.fetchPurchaseOrders();
    } catch (error) {
      console.error('Failed to mark invoice as received:', error);
      toast.error('Failed to mark invoice as received');
    }
  };

  const getStatusVariant = (invoice: any) => {
    const now = Date.now();
    const createdTime = new Date(invoice.createdAt).getTime();
    const hoursElapsed = (now - createdTime) / (1000 * 60 * 60);

    if (invoice.status === 'Complete') {
      return 'default'; // Green
    } else if (hoursElapsed >= 24) {
      return 'destructive'; // Red for expired
    } else {
      return 'secondary'; // Gray for pending
    }
  };

  const getStatusText = (invoice: any) => {
    const now = Date.now();
    const createdTime = new Date(invoice.createdAt).getTime();
    const hoursElapsed = (now - createdTime) / (1000 * 60 * 60);

    if (invoice.status === 'Complete') {
      return 'Completed';
    } else if (hoursElapsed >= 24) {
      return 'Expired';
    } else {
      return 'Pending';
    }
  };

  const getTimeRemaining = (invoice: any) => {
    const now = Date.now();
    const createdTime = new Date(invoice.createdAt).getTime();
    const hoursElapsed = (now - createdTime) / (1000 * 60 * 60);
    
    if (hoursElapsed >= 24) {
      return `Expired ${Math.floor(hoursElapsed - 24)}h ago`;
    } else {
      const remaining = 24 - hoursElapsed;
      return `${Math.floor(remaining)}h remaining`;
    }
  };

  const getHandoverDetails = (invoice: any) => {
    const handoverLog = state.handoverLogs.find(log => log.invoiceId === invoice.id);
    return handoverLog || null;
  };

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Manager Section</h1>
            <p className="text-muted-foreground mt-2">
              Final checkpoint for invoice completion and manager oversight
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-sm">
              {managerInvoices.length} Pending Review
            </Badge>
            {expiredNotifications.length > 0 && (
              <Badge variant="destructive" className="text-sm">
                {expiredNotifications.length} Expired
              </Badge>
            )}
          </div>
        </div>

        {/* Expired Notifications */}
        {expiredNotifications.length > 0 && (
          <Alert className="border-red-200 bg-red-50 dark:bg-red-950">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800 dark:text-red-200">
              <strong>Urgent: {expiredNotifications.length} invoices have expired (24+ hours old)</strong>
              <div className="mt-2 space-y-1">
                {expiredNotifications.slice(0, 3).map((invoice) => (
                  <div key={invoice.id} className="text-sm">
                    • Invoice {invoice.invoiceNumber} (PO: {invoice.poNumber}) - 
                    Floor Manager: {getHandoverDetails(invoice)?.floorManagerName || 'Unknown'}
                  </div>
                ))}
                {expiredNotifications.length > 3 && (
                  <div className="text-sm">• And {expiredNotifications.length - 3} more...</div>
                )}
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending Review</CardTitle>
              <Package className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {managerInvoices.filter(inv => inv.status !== 'Complete').length}
              </div>
              <p className="text-xs text-muted-foreground">
                Awaiting manager action
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Completed Today</CardTitle>
              <CheckCircle className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {state.invoices.filter(invoice => {
                  const today = new Date().toDateString();
                  const receivedDate = invoice.receivedAt ? new Date(invoice.receivedAt).toDateString() : '';
                  return invoice.status === 'Complete' && receivedDate === today;
                }).length}
              </div>
              <p className="text-xs text-muted-foreground">
                Processed today
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Expired Invoices</CardTitle>
              <AlertTriangle className="h-4 w-4 text-red-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                {expiredNotifications.length}
              </div>
              <p className="text-xs text-muted-foreground">
                24+ hours old
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Value</CardTitle>
              <FileText className="h-4 w-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(managerInvoices.reduce((sum, inv) => sum + inv.totalAmount, 0))}
              </div>
              <p className="text-xs text-muted-foreground">
                Pending review value
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Manager Invoices Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Invoice Review Queue</CardTitle>
            <CardDescription>
              Invoices that have passed Quality Testing and are ready for manager review
            </CardDescription>
          </CardHeader>
          <CardContent>
            {managerInvoices.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <h3 className="text-lg font-semibold mb-2">No invoices pending review</h3>
                <p>Invoices will appear here after they pass Quality Testing (IQC)</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice ID</TableHead>
                      <TableHead>PO ID</TableHead>
                      <TableHead>Quantity</TableHead>
                      <TableHead>Items</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Floor Manager</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Time</TableHead>
                      <TableHead>Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {managerInvoices.map((invoice) => {
                      const handoverDetails = getHandoverDetails(invoice);
                      const totalQuantity = invoice.items.reduce((sum, item) => sum + item.quantity, 0);
                      
                      return (
                        <TableRow key={invoice.id}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-muted-foreground" />
                              {invoice.invoiceNumber}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{invoice.poNumber}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <div className="font-medium">{totalQuantity} units</div>
                              <div className="text-muted-foreground">
                                {invoice.receivedStatus}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              {invoice.items.slice(0, 2).map((item, index) => (
                                <div key={index} className="text-sm">
                                  {item.skuName} (Qty: {item.quantity})
                                </div>
                              ))}
                              {invoice.items.length > 2 && (
                                <div className="text-xs text-muted-foreground">
                                  +{invoice.items.length - 2} more items
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <MapPin className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm">
                                {handoverDetails?.storageLocation || 'Not specified'}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {handoverDetails ? (
                              <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                  <User className="h-4 w-4 text-muted-foreground" />
                                  <span className="text-sm font-medium">
                                    {handoverDetails.floorManagerName}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Phone className="h-4 w-4 text-muted-foreground" />
                                  <span className="text-xs text-muted-foreground">
                                    {handoverDetails.floorManagerContact}
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <span className="text-sm text-muted-foreground">Not assigned</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant={getStatusVariant(invoice)}>
                              {getStatusText(invoice)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Timer className="h-4 w-4 text-muted-foreground" />
                              <span className={`text-sm ${
                                getStatusText(invoice) === 'Expired' ? 'text-red-600' : 
                                getStatusText(invoice) === 'Completed' ? 'text-green-600' : 'text-muted-foreground'
                              }`}>
                                {getTimeRemaining(invoice)}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {invoice.status !== 'Complete' && (
                              <Button
                                onClick={() => handleReceivedClick(invoice)}
                                size="sm"
                                className="flex items-center gap-2"
                              >
                                <CheckCircle className="h-4 w-4" />
                                Mark as Received
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Received Confirmation Modal */}
        <Dialog open={showReceivedModal} onOpenChange={setShowReceivedModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm Invoice Received</DialogTitle>
              <DialogDescription>
                Are you sure you want to mark this invoice as received and completed?
              </DialogDescription>
            </DialogHeader>
            
            {selectedInvoice && (
              <div className="space-y-4">
                <div className="p-4 border rounded-lg bg-muted/50">
                  <h4 className="font-semibold mb-2">Invoice Details:</h4>
                  <div className="space-y-1 text-sm">
                    <div><strong>Invoice ID:</strong> {selectedInvoice.invoiceNumber}</div>
                    <div><strong>PO ID:</strong> {selectedInvoice.poNumber}</div>
                    <div><strong>Vendor:</strong> {selectedInvoice.vendorName}</div>
                    <div><strong>Amount:</strong> {formatCurrency(selectedInvoice.totalAmount)}</div>
                    <div><strong>Quality Rating:</strong> {selectedInvoice.qualityRating}/5</div>
                  </div>
                </div>

                <div className="p-4 border rounded-lg bg-blue-50 dark:bg-blue-950">
                  <h4 className="font-semibold mb-2 text-blue-800 dark:text-blue-200">
                    Action Impact:
                  </h4>
                  <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                    <li>• Invoice status will be updated to <strong>Completed</strong></li>
                    <li>• Related POs will be marked as <strong>Received</strong></li>
                    <li>• This action cannot be undone</li>
                  </ul>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReceivedModal(false)}>
                Cancel
              </Button>
              <Button onClick={confirmReceived} className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                Confirm Received
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
};

export default ManagerSection;
